import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { assertServiceOk, BsApiService } from '@brightside-web/desktop/data-access/core-services';
import * as ServiceInventoryActions from './service-inventory.actions';
import { catchError, exhaustMap, map, mergeMap, retry, takeUntil } from 'rxjs/operators';
import { defer, of, timer } from 'rxjs';
import { ServiceInventoryPayload } from './service-inventory.model';
import * as Sentry from '@sentry/angular';

@Injectable()
export class ServiceInventoryEffects {
  private _actions$ = inject(Actions);
  private _bsApi = inject(BsApiService);

  loadServiceInventory$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ServiceInventoryActions.loadServiceInventory),
      exhaustMap(({ requiredKey }) =>
        defer(() => this._bsApi.get<ServiceInventoryPayload>('api-mobile', '/service-inventory')).pipe(
          map((response) => {
            assertServiceOk(response);
            if (!response.payload.items || response.payload.items.length === 0) {
              throw new Error('No services available');
            }
            if (requiredKey && !response.payload.items.some((item) => item.key === requiredKey)) {
              throw new Error(`Required service is not available: ${requiredKey}`);
            }
            return ServiceInventoryActions.loadServiceInventorySuccess({ items: response.payload.items });
          }),
          retry({ count: 2, delay: () => timer(2000) }),
          takeUntil(this._actions$.pipe(ofType(ServiceInventoryActions.resetServiceInventory))),
          catchError((error) => {
            Sentry.captureException(error, { tags: { operation: 'service-inventory-load' } });
            const errorMessage = error instanceof Error ? error.message : String(error);
            return of(ServiceInventoryActions.loadServiceInventoryFailure({ error: errorMessage }));
          })
        )
      )
    )
  );


  deleteServiceInventory$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ServiceInventoryActions.deleteServiceState),
        mergeMap((action) => this._bsApi.del('api-mobile', '/service-inventory', { key: action.key }))
      ),
    { dispatch: false }
  );
}
